import { isEqual } from "lodash";
import { isPresent } from "ts-extras";

import { ALL_COLUMN_OPTIONS } from "src/components/audiences/constants";
import {
  getPropertyNameFromColumn,
  getTraitNameFromColumn,
  UNNAMED_TRAIT_LABEL,
} from "src/components/explore/visual/utils";
import {
  AndOrCondition,
  ColumnType,
  Condition,
  ConditionType,
  isFormulaTraitCondition,
  isInlineTraitCondition,
  isRelatedJourneyEventColumn,
  isMergedColumn,
  isRelatedColumn,
  isTraitColumn,
  isTraitCondition,
  isTransformedColumn,
  ModelSemanticType,
} from "src/types/visual";

import {
  AudienceColumn,
  EventColumn,
  FilterOption,
  GlobalColumn,
  InlineTraitColumn,
  MetadataType,
  PropertyColumn,
  RelationColumn,
  TraitColumn,
  TraitTemplateColumn,
  JourneyColumn,
  MessageEventsColumn,
  RealtimeEventColumn,
} from "./constants";
import { FilterColumnOption } from "./types";
import { IconBoxProps, QuestionIcon } from "@hightouch/htui";
import { SemanticTypeIcons } from "src/pages/schema/graph/schema-icon-maps";
import type { OptionVirtualItem } from "./use-virtual-items";

/**
 * Normalize IDs in a column reference to strings for comparison.
 * This handles the case where some sources provide numeric IDs and others provide string IDs.
 */
export function normalizeColumnReferenceIds<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(normalizeColumnReferenceIds) as T;
  }

  if (typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      // TODO (ajhenry): Remove once rowKey is migrated from DB mappings
      if (key === "rowKey") {
        continue;
      }
      if (
        key === "modelId" &&
        (typeof val === "number" || typeof val === "string")
      ) {
        result[key] = val.toString();
      } else if (key === "path" && Array.isArray(val)) {
        result[key] = val.map((id) => id?.toString());
      } else {
        result[key] = normalizeColumnReferenceIds(val);
      }
    }
    return result as T;
  }

  return value;
}

export function isColumnReferenceEqual(a: unknown, b: unknown): boolean {
  return isEqual(
    normalizeColumnReferenceIds(a),
    normalizeColumnReferenceIds(b),
  );
}

/**
 * Key-order-independent JSON serialization.
 * Produces the same string for { a: 1, b: 2 } and { b: 2, a: 1 }.
 */
export function stableStringify(value: unknown): string {
  if (value === null || value === undefined || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`).join(",")}}`;
}

/**
 * Produces a stable, hashable key for a column reference.
 * Two references that are equal under `isColumnReferenceEqual` will
 * produce the same key, so this can be used for Map/Set lookups.
 */
export function columnReferenceKey(value: unknown): string {
  return stableStringify(normalizeColumnReferenceIds(value));
}

export const getIconProps = (
  type: FilterOption | null | undefined,
  options?: {
    invertColors?: boolean;
    iconOverride?: {
      icon: JSX.Element;
      color: IconBoxProps["color"];
      variant?: IconBoxProps["variant"];
    };
  },
): IconBoxProps | undefined => {
  if (!type) {
    return undefined;
  }

  if (options?.iconOverride) {
    return options.iconOverride;
  }

  switch (type) {
    case FilterOption.All:
      return {
        variant: options?.invertColors ? "inverted" : "default",
        color: GlobalColumn.shade,
        icon: GlobalColumn.icon,
      };
    case FilterOption.Property:
      return {
        variant: options?.invertColors ? "inverted" : "default",
        color: PropertyColumn.shade,
        icon: PropertyColumn.icon,
      };
    case FilterOption.Relation:
      return {
        variant: options?.invertColors ? "inverted" : "default",
        color: RelationColumn.shade,
        icon: RelationColumn.icon,
      };
    case FilterOption.Event:
    case FilterOption.EventProperty:
    case FilterOption.RealtimeEvent:
      return {
        variant: options?.invertColors ? "inverted" : "default",
        color: EventColumn.shade,
        icon:
          type === FilterOption.RealtimeEvent
            ? RealtimeEventColumn.icon
            : EventColumn.icon,
      };
    case FilterOption.Audience:
      return {
        variant: options?.invertColors ? "inverted" : "default",
        color: AudienceColumn.shade,
        icon: AudienceColumn.icon,
      };
    case FilterOption.Trait:
      return {
        variant: options?.invertColors ? "inverted" : "default",
        color: TraitColumn.shade,
        icon: TraitColumn.icon,
      };
    case FilterOption.TraitTemplate:
      return {
        variant: options?.invertColors ? "inverted" : "default",
        color: TraitTemplateColumn.shade,
        icon: TraitTemplateColumn.icon,
      };
    case FilterOption.InlineTrait:
      return {
        variant: options?.invertColors ? "inverted" : "default",
        color: InlineTraitColumn.shade,
        icon: InlineTraitColumn.icon,
      };
    case FilterOption.Journey:
      return {
        variant: options?.invertColors ? "inverted" : "default",
        color: JourneyColumn.shade,
        icon: JourneyColumn.icon,
      };
    case FilterOption.MessageEvent:
      return {
        variant: options?.invertColors ? "inverted" : "default",
        color: MessageEventsColumn.shade,
        icon: MessageEventsColumn.icon,
      };
  }
};

export const getSearchPlaceholderText = (filterType: FilterOption) => {
  switch (filterType) {
    case FilterOption.All:
      return "Search all filters...";
    case FilterOption.Property:
      return "Search properties...";
    case FilterOption.Relation:
      return "Search relations...";
    case FilterOption.Event:
    case FilterOption.EventProperty:
    case FilterOption.RealtimeEvent:
      return "Search events...";
    case FilterOption.Audience:
      return "Search audiences...";
    case FilterOption.Trait:
    case FilterOption.TraitTemplate:
    case FilterOption.InlineTrait:
      return "Search traits...";
    case FilterOption.Journey:
      return "Search journeys...";
    case FilterOption.MessageEvent:
      return "Search message events...";
  }
};

export const isColumnSelected = <TCondition extends Condition>(
  condition: TCondition | undefined,
  column: FilterColumnOption,
) => {
  if (!condition) {
    return false;
  }

  switch (column.type) {
    case FilterOption.Property:
      return (
        condition.type === ConditionType.Property &&
        isColumnReferenceEqual(condition.property, column.value)
      );
    case FilterOption.Relation:
      return (
        condition.type === ConditionType.NumberOf &&
        condition.relationshipId?.toString() ===
          column.value.relationshipId?.toString()
      );
    case FilterOption.Event:
    case FilterOption.RealtimeEvent:
      return (
        condition.type === ConditionType.Event &&
        condition.eventModelId?.toString() ===
          column.value.eventModelId?.toString() &&
        condition.relationshipId?.toString() ===
          column.value.relationshipId?.toString()
      );
    case FilterOption.MessageEvent:
      return (
        condition.type === ConditionType.Event &&
        !column.value.funnelCondition &&
        condition.eventModelId?.toString() ===
          column.value.eventModelId?.toString() &&
        condition.relationshipId?.toString() ===
          column.value.relationshipId?.toString()
      );
    case FilterOption.EventProperty:
      return (
        condition.type === ConditionType.Property &&
        isRelatedJourneyEventColumn(condition.property) &&
        condition.property.column.modelId?.toString() ===
          column.value.modelId?.toString()
      );
    case FilterOption.Audience:
      return (
        condition.type === ConditionType.SegmentSet &&
        condition.modelId?.toString() === column.value.modelId?.toString()
      );
    case FilterOption.Trait:
    case FilterOption.TraitTemplate:
      return (
        condition.type === ConditionType.Property &&
        (isRelatedColumn(condition.property) ||
          isTransformedColumn(condition.property)) &&
        isTraitColumn(condition.property.column) &&
        column.value.column.type === "trait" &&
        condition.property.column.traitDefinitionId ===
          column.value.column.traitDefinitionId
      );
    default:
      return false;
  }
};

/**
 * Pre-built lookup indices for `getSelectedColumnFromIndex`. Building the
 * indices is O(columnOptions); querying is O(1) per lookup. Callers that
 * resolve many conditions against the same `columnOptions` (e.g. one per
 * condition in an audience builder render) should build the index once and
 * reuse it across calls.
 *
 * Each lookup gets its own Map keyed on the exact field combination the
 * original `.find()` predicate compared. The `Property` branch goes through
 * `columnReferenceKey` since the original predicate used the structural
 * `isColumnReferenceEqual`. These agree on the plain-JSON `ColumnReference`
 * shapes used in production; they would diverge only on objects with
 * explicit-undefined keys, which column references don't carry.
 */
export type SelectedColumnIndex = {
  byTraitId: Map<string, FilterColumnOption>;
  eventPropertyByModelId: Map<string, FilterColumnOption>;
  propertyByReferenceKey: Map<string, FilterColumnOption>;
  relationByRelationshipId: Map<string, FilterColumnOption>;
  eventByModelAndRelationshipId: Map<string, FilterColumnOption>;
  audienceByModelId: Map<string, FilterColumnOption>;
  journeyByJourneyId: Map<string, FilterColumnOption>;
};

const eventLookupKey = (
  eventModelId: string | number | undefined | null,
  relationshipId: string | number | undefined | null,
): string => `${eventModelId ?? ""}::${relationshipId ?? ""}`;

export const buildSelectedColumnIndex = (
  columnOptions: FilterColumnOption[],
): SelectedColumnIndex => {
  const index: SelectedColumnIndex = {
    byTraitId: new Map(),
    eventPropertyByModelId: new Map(),
    propertyByReferenceKey: new Map(),
    relationByRelationshipId: new Map(),
    eventByModelAndRelationshipId: new Map(),
    audienceByModelId: new Map(),
    journeyByJourneyId: new Map(),
  };

  // Preserve first-match-wins to match the original `.find()` semantics.
  const setIfAbsent = <K, V>(map: Map<K, V>, key: K, value: V) => {
    if (!map.has(key)) map.set(key, value);
  };

  for (const option of columnOptions) {
    switch (option.type) {
      case FilterOption.Trait:
      case FilterOption.TraitTemplate: {
        const column = option.value.column;
        if (isTraitColumn(column) || isTransformedColumn(column)) {
          const traitDefinitionId = (column as { traitDefinitionId?: string })
            .traitDefinitionId;
          if (traitDefinitionId) {
            setIfAbsent(index.byTraitId, traitDefinitionId, option);
          }
        }
        break;
      }
      case FilterOption.EventProperty: {
        const modelId = option.value.modelId?.toString();
        if (modelId !== undefined) {
          setIfAbsent(index.eventPropertyByModelId, modelId, option);
        }
        break;
      }
      case FilterOption.Property: {
        // `columnReferenceKey` handles both string and structured values,
        // matching the behavior of `isColumnReferenceEqual` in the
        // original `.find()` predicate.
        setIfAbsent(
          index.propertyByReferenceKey,
          columnReferenceKey(option.value),
          option,
        );
        break;
      }
      case FilterOption.Relation: {
        const relationshipId = option.value.relationshipId?.toString();
        if (relationshipId !== undefined) {
          setIfAbsent(index.relationByRelationshipId, relationshipId, option);
        }
        break;
      }
      case FilterOption.Event:
      case FilterOption.RealtimeEvent:
      case FilterOption.MessageEvent: {
        const key = eventLookupKey(
          option.value.eventModelId,
          option.value.relationshipId,
        );
        setIfAbsent(index.eventByModelAndRelationshipId, key, option);
        break;
      }
      case FilterOption.Audience: {
        const modelId = option.value.modelId?.toString();
        if (modelId !== undefined) {
          setIfAbsent(index.audienceByModelId, modelId, option);
        }
        break;
      }
      case FilterOption.Journey: {
        const journeyId = option.value.journeyId;
        if (journeyId !== undefined) {
          setIfAbsent(index.journeyByJourneyId, journeyId, option);
        }
        break;
      }
      default:
        break;
    }
  }

  return index;
};

export const getSelectedColumnFromIndex = (
  condition: Condition | undefined,
  index: SelectedColumnIndex,
): FilterColumnOption | null | undefined => {
  if (!condition) {
    return null;
  }

  switch (condition.type) {
    case ConditionType.Property: {
      /** INLINE TRAIT COLUMN */
      const traitId =
        (isTransformedColumn(condition.property) ||
          isRelatedColumn(condition.property)) &&
        isTraitColumn(condition.property.column)
          ? condition.property.column.traitDefinitionId
          : null;

      if (traitId) {
        return index.byTraitId.get(traitId);
      }

      /** JOURNEY EVENT COLUMN */
      const eventModelId = isRelatedJourneyEventColumn(condition.property)
        ? condition.property.column.modelId
        : null;

      if (eventModelId) {
        return index.eventPropertyByModelId.get(eventModelId.toString());
      }

      return index.propertyByReferenceKey.get(
        columnReferenceKey(condition.property),
      );
    }
    case ConditionType.NumberOf: {
      const relationshipId = condition.relationshipId?.toString();
      if (relationshipId === undefined) return undefined;
      return index.relationByRelationshipId.get(relationshipId);
    }
    case ConditionType.Event:
      return index.eventByModelAndRelationshipId.get(
        eventLookupKey(condition.eventModelId, condition.relationshipId),
      );
    case ConditionType.SegmentSet: {
      const modelId = condition.modelId?.toString();
      if (modelId === undefined) return undefined;
      return index.audienceByModelId.get(modelId);
    }
    case ConditionType.JourneySet: {
      const journeyId = condition.journeyIds[0];
      if (journeyId === undefined) return undefined;
      return index.journeyByJourneyId.get(journeyId);
    }

    default:
      return null;
  }
};

export const getValueText = (condition: Condition) => {
  switch (condition.type) {
    case ConditionType.Property:
      if (typeof condition.property === "string") {
        return condition.property;
      }

      return getPropertyNameFromColumn(condition.property);
  }

  return undefined;
};

export const getSetupLink = (type: FilterOption, parentModelId?: number) => {
  switch (type) {
    case FilterOption.All:
    case FilterOption.Property:
    case FilterOption.Trait:
    case FilterOption.TraitTemplate:
      return isPresent(parentModelId)
        ? `/schema/parent-models/${parentModelId}`
        : "/schema/parent-models";
    case FilterOption.Relation:
      return "/schema/related-models/new";
    case FilterOption.Event:
    case FilterOption.EventProperty:
    case FilterOption.RealtimeEvent:
      return "/schema/events/new";
    case FilterOption.Audience:
      return "/audiences/new";
    case FilterOption.Journey:
      return "/journeys/new";
    case FilterOption.InlineTrait:
    case FilterOption.MessageEvent:
      return ""; // should never reach here because there is nothing to set up
  }
};

export const getMetadata = (column: FilterColumnOption | undefined | null) => {
  if (!column) {
    return null;
  }

  if (
    column.type === FilterOption.Trait ||
    column.type === FilterOption.TraitTemplate
  ) {
    // trait column
    return {
      modelName:
        column.value.type === "transformed"
          ? (column.parentModel?.name ?? "")
          : (column.relatedModel?.name ?? ""),
      type: column.relatedModel?.event
        ? MetadataType.Event
        : MetadataType.Relation,
    };
  } else if (
    column.type === FilterOption.Property &&
    isMergedColumn(column.value)
  ) {
    // merged column
    return {
      modelName: column.modelName,
      type: MetadataType.MergedColumn,
    };
  }

  return null;
};

export const getModelName = (
  column: FilterColumnOption | undefined | null,
): string | null | undefined => {
  if (!column) {
    return null;
  }

  switch (column.type) {
    case FilterOption.Trait:
      return column.relatedModel?.name;
    case FilterOption.Property:
      return column.modelName;
    default:
      return null;
  }
};

export function getRelationshipLabel(
  relationshipName: string | null,
  modelName: string,
) {
  if (!relationshipName && !modelName) {
    return "";
  }

  if (!relationshipName) {
    return modelName;
  }

  return `${modelName} (via ${relationshipName})`;
}

export function getPropertyLabel(
  alias: string | null,
  name: string,
  relationshipNames?: string[] | null,
) {
  let label = alias || name;
  const relationshipName = relationshipNames?.at(-1);
  if (relationshipName) {
    label += ` (via ${relationshipName})`;
  }
  return label;
}

export function countRelationsByModel(
  relations: { to_model: { name: string } }[],
): Record<string, number> {
  return relations.reduce<Record<string, number>>((accum, event) => {
    const modelName = event.to_model.name;
    if (!(modelName in accum)) {
      accum[modelName] = 0;
    }

    accum[modelName]! += 1;
    return accum;
  }, {});
}

export const getColumnTypeMetadata = (type: ColumnType) => {
  return ALL_COLUMN_OPTIONS.find((option) => option.value === type);
};

export const getColumnIcon = (option: {
  type: FilterOption;
  columnType: ColumnType | undefined;
  semanticType: ModelSemanticType | null;
}) => {
  switch (option.type) {
    case FilterOption.Property: {
      if (!option.columnType) return QuestionIcon;

      return getColumnTypeMetadata(option.columnType)?.icon ?? QuestionIcon;
    }
    case FilterOption.Relation:
    case FilterOption.Event:
      return option.semanticType
        ? SemanticTypeIcons[option.semanticType]
        : null;

    default:
      return null;
  }
};

/**
 * Searches a condition tree (or VisualQueryFilter root) for a condition with
 * the given id. Accepts `unknown` so callers can pass a raw filter root or a
 * parsed JSON blob — both use the same `{ id?, conditions? }` shape.
 */
export const findConditionById = (
  tree: unknown,
  id: string,
): AndOrCondition<Condition> | undefined => {
  if (!tree || typeof tree !== "object") return undefined;
  const node = tree as {
    id?: string;
    conditions?: unknown[];
  };
  if (node.id === id) {
    return node as unknown as AndOrCondition<Condition>;
  }
  if (Array.isArray(node.conditions)) {
    for (const child of node.conditions) {
      const found = findConditionById(child, id);
      if (found) return found;
    }
  }
  return undefined;
};

/**
 * Metadata passed to resolve id-only conditions (Event, NumberOf, SegmentSet,
 * JourneySet) to their human-readable names. Shape matches what
 * `useFilterableOptionsContext` exposes. Omit to fall back to generic labels.
 */
export type ConditionDisplayMetadata = {
  relationships?: {
    id: string;
    to_model: { id: string; name: string };
  }[];
  relatedAudiences?: { id: string; name: string }[];
  journeys?: { id: string; name: string }[];
  traits?: { id: string; name: string }[];
};

/**
 * Returns the name of the subject a condition filters on — e.g. "gender",
 * "Purchases", "VIP Users". Operators, values, and sub-filters are
 * intentionally omitted: the focused-filter card's subtitle already names the
 * filter type, and the card links back to the builder for full details. In
 * practice the focused-filter UI only nudges with leaf conditions; an And/Or
 * wrapper falls through to the default.
 */
export const getConditionLabel = (
  condition: AndOrCondition<Condition>,
  metadata?: ConditionDisplayMetadata,
): string => {
  switch (condition.type) {
    case ConditionType.Property: {
      const name = getValueText(condition);
      if (name) return name;
      if (
        isTraitCondition(condition) ||
        isFormulaTraitCondition(condition) ||
        isInlineTraitCondition(condition)
      ) {
        const traitName = getTraitNameFromColumn(
          condition.property,
          metadata?.traits ?? [],
        );
        return traitName ?? UNNAMED_TRAIT_LABEL;
      }
      return "Property";
    }
    case ConditionType.ReferenceProperty: {
      const property = condition.property;
      const name =
        typeof property === "string"
          ? property
          : getPropertyNameFromColumn(property);
      return name || "Property";
    }
    case ConditionType.Event: {
      const rel = metadata?.relationships?.find(
        (r) => r.to_model.id.toString() === condition.eventModelId?.toString(),
      );
      return rel?.to_model.name ?? "Event";
    }
    case ConditionType.NumberOf: {
      const rel = metadata?.relationships?.find(
        (r) => r.id.toString() === condition.relationshipId?.toString(),
      );
      return rel?.to_model.name ?? "Related model";
    }
    case ConditionType.SegmentSet: {
      const audience = metadata?.relatedAudiences?.find(
        ({ id }) => id.toString() === condition.modelId?.toString(),
      );
      return audience?.name ?? "Audience";
    }
    case ConditionType.JourneySet: {
      const journey = metadata?.journeys?.find(
        ({ id }) => id.toString() === condition.journeyIds?.[0]?.toString(),
      );
      return journey?.name ?? "Journey";
    }
    default:
      return "Condition";
  }
};

export type FocusedFilterConditionType =
  | "property"
  | "trait"
  | "event"
  | "number_of"
  | "segment_set"
  | "journey_set"
  | "funnel"
  | "reference_property";

export type FocusedFilterSource = "insights" | "manual";

export type FocusedFilterValue = {
  label: string;
  conditionId?: string;
  conditionType?: FocusedFilterConditionType;
  source?: FocusedFilterSource;
};

/** Overrides threaded through agent submit handlers for the focused-filter flow. */
export type FocusedSubmitOverrides = {
  focusedConditions?: AndOrCondition<Condition>;
  focusedLabel?: string;
  source?: FocusedFilterSource;
};

/**
 * Maps a condition to the `conditionType` value stored in focusedFilter metadata.
 * Trait-based property conditions get their own type so the card renders with
 * the trait icon rather than the generic property icon.
 */
export const describeFocusedConditionType = (
  condition: AndOrCondition<Condition>,
): FocusedFilterConditionType => {
  if (
    condition.type === ConditionType.Property &&
    (isTraitCondition(condition) ||
      isFormulaTraitCondition(condition) ||
      isInlineTraitCondition(condition))
  ) {
    return "trait";
  }
  return condition.type as FocusedFilterConditionType;
};

/**
 * Builds the `focusedFilter` metadata value sent with an LLM user message.
 * Returns undefined when there are no focused conditions, so the caller can
 * conditionally spread: `...(value ? { focusedFilter: value } : {})`. The
 * label is pre-resolved at the nudge site (where metadata is available).
 */
export const buildFocusedFilterValue = (
  conditions: AndOrCondition<Condition> | null | undefined,
  label: string | null | undefined,
  source: FocusedFilterSource,
): FocusedFilterValue | undefined => {
  if (!conditions || !label) return undefined;
  return {
    label,
    conditionType: describeFocusedConditionType(conditions),
    source,
    ...(conditions.id ? { conditionId: conditions.id } : {}),
  };
};

/**
 * Step to the next non-disabled option in `direction`, clamping at the list
 * ends. When nothing is highlighted yet (currentIndex is -1), ArrowDown
 * starts at the first option and ArrowUp at the last.
 */
export function getNextEnabledOptionItem(
  optionItems: OptionVirtualItem<FilterColumnOption>[],
  currentIndex: number,
  direction: "up" | "down",
): OptionVirtualItem<FilterColumnOption> | undefined {
  const start =
    currentIndex === -1
      ? direction === "down"
        ? -1
        : optionItems.length
      : currentIndex;

  const directionOffset = direction === "down" ? 1 : -1;
  for (
    let next = start + directionOffset;
    next >= 0 && next < optionItems.length;
    next += directionOffset
  ) {
    if (!optionItems[next]?.option.isDisabled) {
      return optionItems[next];
    }
  }

  return undefined;
}
